import { useEffect, useRef } from "react";

/**
 * spec08 FR18: what the Android back gesture closes, in order: an open select list, the
 * topmost dialog (like Esc), then the newest registered back step (an overlay, a notes pane).
 * Returns false when nothing was closed, so the caller goes back in the history.
 */
const steps: Array<{ current: () => void }> = [];

export function handleBack(): boolean {
  const list = document.querySelector('[role="listbox"][data-state="open"]');
  if (list) {
    document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", bubbles: true }));
    return true;
  }
  const dialog = [...document.querySelectorAll("dialog[open]")].at(-1);
  if (dialog) {
    // The Dialog component closes on `cancel`, the event Esc fires.
    dialog.dispatchEvent(new Event("cancel", { cancelable: true }));
    return true;
  }
  const step = steps.at(-1);
  if (step) {
    step.current();
    return true;
  }
  return false;
}

/** Registers a back step while `active` (e.g. the notebook overlay is open). */
export function useBackStep(active: boolean, onBack: () => void) {
  const ref = useRef(onBack);
  ref.current = onBack;
  useEffect(() => {
    if (!active) return;
    const step = { current: () => ref.current() };
    steps.push(step);
    return () => {
      steps.splice(steps.indexOf(step), 1);
    };
  }, [active]);
}
